import React from "react";
import { View, StyleSheet, Image } from "react-native";
import { colors, layout } from "@/constants";
import Text from "./base/Text";
import Icon, { IconSize } from "./base/Icon";

/**
 * User data interface matching Firebase Auth user
 */
interface UserData {
  displayName?: string | null;
  email?: string | null;
  photoURL?: string | null;
}

/**
 * Props interface for the UserProfile component
 */
interface UserProfileProps {
  /** User data object */
  user: UserData;
  /** Whether to show the email address (default: true) */
  showEmail?: boolean;
  /** Size variant for the profile display */
  size?: "compact" | "full";
}

/**
 * A reusable user profile display component.
 * Shows user photo/avatar, name, and optionally email.
 *
 * @param props - The component props
 * @returns A user profile display with consistent styling
 */
export default function UserProfile({
  user,
  showEmail = true,
  size = "full"
}: UserProfileProps) {
  const photoSize = size === "compact" ? layout.size.lg : layout.size.xl;
  const iconSize: IconSize = size === "compact" ? "large" : "xlarge";

  return (
    <View style={styles.container}>
      {/* User Photo or Default Icon */}
      {user?.photoURL ? (
        <Image
          source={{ uri: user.photoURL }}
          style={[
            styles.userPhoto,
            {
              width: photoSize,
              height: photoSize,
              borderRadius: photoSize / 2
            }
          ]}
        />
      ) : (
        <Icon
          name="account_circle"
          size={iconSize}
          style={{ color: colors.primary }}
        />
      )}

      {/* User Details */}
      <View style={styles.userDetails}>
        <Text
          size={size === "compact" ? "small" : "medium"}
          style={styles.userName}
        >
          {user.displayName || "User"}
        </Text>
        {showEmail && user.email && (
          <Text size="small" color="secondary" style={styles.userEmail}>
            {user.email}
          </Text>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center"
  },
  userDetails: {
    marginLeft: layout.space.md,
    flex: 1
  },
  userName: {
    marginBottom: layout.space.xs
  },
  userEmail: {},
  userPhoto: {
    // Dynamic styles applied inline based on size prop
  }
});
